import { useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { apiFetch } from '../../lib/api-client';

interface AuthGuardProps {
  children: ReactNode;
  loginPath?: string;
}

/**
 * Cloudflare Access owns the browser session. This guard only bootstraps the
 * D1-backed application identity and authorization context.
 */
export function AuthGuard({ children }: AuthGuardProps) {
  const location = useLocation();
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function checkAuth() {
      try {
        const response = await apiFetch<any>('/api/admin/session');
        const user = response?.data?.user;
        if (!user) throw new Error('Admin identity was not returned');
        localStorage.setItem('fc_admin_auth', JSON.stringify(user));
        setAuthed(true);
      } catch (e: any) {
        localStorage.removeItem('fc_admin_auth');
        setError(e?.message || 'Không thể xác minh quyền truy cập');
      } finally {
        setChecking(false);
      }
    }

    checkAuth();
  }, [location.pathname]);

  if (checking && !location.pathname.endsWith('/login')) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="spinner" />
        <span className="ml-3 text-muted-foreground text-sm">Checking authentication...</span>
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <div className="max-w-md text-center">
          <h1 className="text-xl font-semibold">Không có quyền truy cập Admin</h1>
          <p className="mt-2 text-sm text-muted-foreground">{error}</p>
          <button className="mt-4 rounded-lg bg-primary px-4 py-2 text-primary-foreground" onClick={() => window.location.reload()}>
            Thử lại qua Cloudflare Access
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
